import React, { FunctionComponent, ReactNode, MouseEvent, useState, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';

import { SeoLink_linkData } from './__generated__/SeoLink_linkData.graphql';
import { CustomLinkData } from '../types';

export const PARTIALLY_LINKABLE_LINK = 'PARTIALLY_LINKABLE_LINK';
export const ALWAYS_LINKABLE_LINK = 'ALWAYS_LINKABLE_LINK';
export const NO_CONTENT_LINK = 'NO_CONTENT_LINK';

type Props = {
    children?: ReactNode;
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    linkData: SeoLink_linkData | CustomLinkData;
    dataTn: string;
    dataPk?: string;
    className?: string;
    variant?: typeof PARTIALLY_LINKABLE_LINK | typeof ALWAYS_LINKABLE_LINK | typeof NO_CONTENT_LINK;
    style?: React.CSSProperties;
    key?: string;
    role?: string;
    tabIndex?: number;
    target?: string;
    ariaLabel?: string;
    ariaDescribedBy?: string;
    isAmp?: boolean;
};

export const SeoLinkComponent: FunctionComponent<Props> = ({
    children,
    linkData,
    onClick = () => {},
    dataTn,
    dataPk,
    className,
    variant = ALWAYS_LINKABLE_LINK,
    style,
    key,
    role,
    tabIndex = 0,
    target,
    ariaLabel,
    ariaDescribedBy,
    isAmp,
}) => {
    const { path, isLinkable } = linkData;
    const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
    const [hasHref, setHasHref] = useState(false);
    const alwaysLinkableLink = useRef<HTMLAnchorElement>(null);
    const addHref = (): void => {
        if (path && alwaysLinkableLink?.current) {
            setHasHref(true);
            alwaysLinkableLink.current.href = path;
        }
    };

    if (!path) {
        return <>{children}</>;
    }

    if (isLinkable) {
        return (
            <a
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                role={role}
                href={path}
                onClick={onClick}
                data-tn={dataTn}
                data-pk={dataPk}
                className={className}
                style={style}
                key={key}
                ref={alwaysLinkableLink}
                rel={rel}
                target={target}
            >
                {children}
            </a>
        );
    }

    if (variant === NO_CONTENT_LINK) {
        return null;
    }

    if (variant === PARTIALLY_LINKABLE_LINK) {
        return (
            <span data-tn={dataTn} data-pk={dataPk} className={className} style={style} key={key}>
                {children}
            </span>
        );
    }

    if (variant === ALWAYS_LINKABLE_LINK) {
        return (
            <a
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                onClick={(e: MouseEvent<HTMLElement>) => {
                    onClick(e);
                    addHref();
                }}
                tabIndex={hasHref ? undefined : tabIndex} // links w/o href need tabindex
                role={role}
                onFocus={addHref}
                onMouseOver={addHref}
                onTouchStart={addHref}
                // @ts-ignore
                on={isAmp && `tap:AMP.navigateTo(url="${path}")`} // https://amp.dev/documentation/guides-and-tutorials/learn/amp-actions-and-events/#special-targets
                data-tn={dataTn}
                data-pk={dataPk}
                className={className}
                style={style}
                ref={alwaysLinkableLink}
                key={key}
                rel={rel}
                target={target}
            >
                {children}
            </a>
        );
    }

    return null;
};

export const SeoLink = createFragmentContainer(SeoLinkComponent, {
    linkData: graphql`
        fragment SeoLink_linkData on LinkData {
            path
            isLinkable
        }
    `,
});
